// ----------------------------------------------------------------------- imports externos
import { lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

// ----------------------------------------------------------------------- imports internos
import { FLOTABOT, SECCIONES, puedeEditar } from "./secciones";
import { abrirSesionEnFlotaBot } from "../../services/ssoFlotaBot";
import { temaActual } from "../../shared/tema";
import { estiloParaPanel } from "./temasUat";

/* El editor del artículo, el MISMO de la ficha de Stock. Se carga recién la
   primera vez que una sede pide editar: el armazón no paga por los
   formularios en cada pantalla. */
const FormStock = lazy(() => import("../formularios/FormStock"));

/* Lo que se le devuelve a la sede: los campos que dibuja su tabla (y los
   demás de la ficha, por si mañana dibuja otro). */
const PARA_LA_SEDE = ["id", "descripcion", "marca", "unidad", "tipo", "detalle", "codigoProveedor"];
const paraLaSede = (a) =>
  Object.fromEntries(PARA_LA_SEDE.map((k) => [k, a?.[k] ?? null]));

/* Las páginas del panel de FlotaBot, mostradas ACÁ ADENTRO.
 *
 * Va en un iframe porque FlotaBot es otra aplicación, en otro servidor, con sus
 * propias páginas. Reescribir el mapa, los reportes y las geofences acá sería
 * rehacerlos desde cero.
 *
 * ── LO QUE SE ABRIÓ, NO SE CIERRA ───────────────────────────────────────────
 *
 * VIVE EN EL ARMAZÓN Y NO EN LA RUTA, y ésa es la parte que hace que funcione.
 * Si estuviera en el componente de la ruta, salir a cualquier otra pantalla lo
 * desmontaría y al volver habría que recargar todo — que es exactamente el
 * problema que vino a resolver. Acá sobrevive a toda la navegación de adentro
 * de la app.
 *
 * Cada sección visitada queda montada y sólo se esconde. Volver al mapa después
 * de pasar por el reporte es instantáneo: los tiles ya están, el mapa ya está
 * centrado donde lo dejaste, y el reporte no vuelve a pedir todo.
 *
 * Sí, ocupa más memoria: son documentos enteros vivos. Se acota de dos maneras
 * —sólo las que se visitaron de verdad, y con un tope— porque un iframe con un
 * mapa adentro no es gratis y tener siete abiertos en un celular es pedir que
 * el navegador los descarte solo, justamente lo que esto vino a evitar.
 *
 * Lo que NO se hace es precargar todo al arrancar. Sería tentador ("que vaya
 * cacheando mapa y después reporte"), pero en el celular significa bajar dos
 * aplicaciones enteras antes de que la persona toque nada, compitiendo por la
 * red con la pantalla que sí está mirando. Se carga lo que se abre, y una vez
 * abierto no se tira.
 */

// Cuántas se mantienen vivas a la vez. Con las tres o cuatro que alguien usa en
// una jornada alcanza; más que eso es memoria retenida para algo que no se va a
// mirar.
const TOPE_VIVAS = 4;

/* EL TEMA DEL HUB TIENE QUE LLEGARLE AL PANEL.
 *
 * FlotaBot es otra aplicación en otro dominio, con su propia preferencia de
 * tema guardada aparte. Hasta ahora los dos lados decidían por su cuenta: el
 * hub en claro y el panel en oscuro, o al revés. No es un detalle estético —
 * es LA costura que hace que esto se vea como dos aplicaciones pegadas.
 *
 * Se manda de dos formas, y las dos hacen falta:
 *
 *   en la URL      resuelve el PRIMER pintado. Sin esto habría un destello del
 *                  tema equivocado antes de que llegue el mensaje.
 *   por mensaje    resuelve los cambios posteriores SIN RECARGAR. Cambiar el
 *                  `src` recargaría el iframe, y eso tira el mapa que ya estaba
 *                  centrado — justo lo que este componente existe para evitar.
 */
/* La lectura del tema vive en `shared/tema`: la usa también el tablero de
   Presidencia para elegir la paleta de sus gráficos, y dos copias es cómo un
   día el panel y el tablero terminan pintados distinto. */

/* «Ver como», del otro lado del iframe.
 *
 * Los paneles van con la sesión REAL —es otra aplicación, en otro servidor—
 * así que se dibujaban siempre con los permisos de quien mira. Probar «sólo
 * furgones» desde el hub mostraba los dos talleres, y eso ya mandó dos veces
 * a revisar un permiso que estaba bien.
 *
 * Va en la URL y NO por `postMessage`, aunque el tema sí vaya por mensaje. El
 * tema lo aplica una hoja de estilos; esto decide QUÉ DATOS se piden y qué
 * botones existen, o sea que se resuelve en el servidor al armar la página.
 * Un mensaje llegaría después de que la página ya se dibujó con lo otro.
 *
 * Y por eso cambiar de persona RECARGA el panel, que es lo correcto: una
 * ticketera que se quedó con la lista anterior mientras la franja dice otro
 * nombre es exactamente la clase de mentira que esto viene a sacar.
 *
 * NO DA ACCESO A NADA: sólo puede mostrar MENOS. FlotaBot igual lo verifica de
 * su lado y lo ignora para cualquiera que no sea la cuenta que ya lo ve todo
 * — el parámetro se puede escribir a mano, así que la puerta no puede estar
 * de este lado. */
/* EL ÍTEM QUE PIDIÓ EL BUSCADOR (Igna, 02/10: «que se abra en el ítem
 * exacto, no solo la pantalla»).
 *
 * El buscador manda `/panel/movimientos?ver=8112`. Ese `ver` se le pasa al
 * marco en su URL —FlotaBot arma la página ya parada en ese viaje, con el
 * período que lo contiene— y por eso SÍ recarga el marco, a diferencia del
 * tema: lo que se pidió es justamente otra página, no la misma con otro color.
 *
 * Se recarga sólo el marco de esa sección, y sólo cuando llega un pedido
 * nuevo. Cada pedido tiene su número (`vez`) y el marco se remonta con él: buscar
 * dos veces el mismo viaje lo vuelve a abrir aunque el id sea el mismo, que es
 * lo que espera quien lo volvió a buscar porque se fue de la fila.
 *
 * Después de tomarlo, el armazón saca `?ver=` de la URL del hub
 * (`onPedidoTomado`): recargar el hub no tiene que volver a mover el panel.
 */
const PanelesFlotaBot = ({ seccion, verComoMail, pedido = "", onPedidoTomado, permisos = null }) => {
  const [sesionLista, setSesionLista] = useState(false);
  const [cargadas, setCargadas] = useState({});
  // sección → { ver, vez }: el último ítem pedido para cada marco.
  const [pedidos, setPedidos] = useState({});
  const vez = useRef(0);

  useEffect(() => {
    if (!seccion || !pedido) return;
    vez.current += 1;
    const n = vez.current;
    setPedidos((p) => ({ ...p, [seccion]: { ver: String(pedido), vez: n } }));
    setCargadas((c) => ({ ...c, [seccion]: false }));
    onPedidoTomado?.();
  }, [seccion, pedido, onPedidoTomado]);
  // Las que se visitaron, en orden de uso: la última al final. Es un ref y no
  // estado porque cambiarlo no tiene que redibujar por sí solo.
  const visitadas = useRef([]);

  const item = SECCIONES.flatMap((g) => g.items).find(
    (i) => i.id === seccion && i.donde === "flotabot",
  );

  // El tema con el que se pintó cada iframe la primera vez. Se congela por
  // marco: si cambiara acá, el `src` cambiaría y el iframe se recargaría.
  const temaInicial = useRef(temaActual());
  // Y el nombre del tema con identidad propia (Industrial, Papel…), si hay
  // uno. Con él en la URL el panel puede nacer vestido con lo que guardó la
  // vez anterior, sin esperar el mensaje. Congelado por lo mismo que el tema.
  const estiloInicial = useRef(estiloParaPanel()?.estilo || "");
  const marcos = useRef({});

  const avisarTema = useCallback((tema) => {
    Object.values(marcos.current).forEach((el) => {
      // El origen va explícito y no "*": un mensaje con comodín se lo puede
      // leer cualquier cosa que llegue a estar en ese marco.
      try {
        // Con un tema de identidad propia van además su nombre y sus
        // valores. Sin tema —producción— el mensaje es el de siempre.
        el?.contentWindow?.postMessage(
          { fibot: "tema", tema, ...(estiloParaPanel() || {}) },
          FLOTABOT,
        );
      } catch (e) {
        // Un marco que todavía no cargó no puede recibirlo, y no pasa nada:
        // ése ya nació con el tema en la URL.
      }
    });
  }, []);

  // El hub cambia `data-theme` en <html>; el observer es lo que evita tener que
  // enganchar esto a cada lugar donde se cambia el tema — incluido el modo
  // automático, que lo cambia solo cuando el sistema pasa a oscuro.
  useEffect(() => {
    const obs = new MutationObserver(() => avisarTema(temaActual()));
    obs.observe(document.documentElement, {
      attributes: true,
      // `data-tema` es el tema con identidad propia: cambiar de Papel a Suave
      // no mueve `data-theme` (los dos son claros) y el panel no se enteraba.
      // `style` es por la opacidad de las tarjetas: el deslizador
      // la escribe en el `style` de la raíz, y los paneles la reciben con el
      // resto del estilo.
      attributeFilter: ["data-theme", "data-tema", "style"],
    });
    const mq = window.matchMedia?.("(prefers-color-scheme: dark)");
    const alSistema = () => avisarTema(temaActual());
    mq?.addEventListener?.("change", alSistema);
    return () => {
      obs.disconnect();
      mq?.removeEventListener?.("change", alSistema);
    };
  }, [avisarTema]);

  /* ENGANCHES → ARRIBOS BITREN (Lautaro, 19/10: «QUE DETECTES CUANDO EL
   * TITULAR ENGANCHE LA 2DA PARTE EN MI PLANILLA DE COMBINE Y CENTRE
   * AUTOMATICAMENTE […] CUANDO YO GENERO EL CORREO DEL ENGANCHE»).
   *
   * La pantalla de Enganches es de FlotaBot, pero la planilla es del hub: al
   * abrir «Generar correo» FlotaBot avisa por mensaje qué enganche es, el hub
   * unifica el bitren en ARRIBOS BITREN con la sesión de quien mira
   * (`services/arribosUnificar`, con su copia al Historial) y le contesta la
   * línea que la pantalla muestra arriba del correo. El correo no espera a
   * nada de esto ni depende de que salga bien.
   *
   * Y AL REVÉS HACIA RÍO GRANDE (Lautaro, 21/10: «en vez de unir en la
   * planilla (como los que salen de rgd) que se separe y pongas al chofer
   * que lleve la 2da parte»): el correo de un enganche «sur» separa el
   * bitren en SALIDAS BITREN y pone abajo la 2da parte
   * (`services/salidasSeparar`). La respuesta es el mismo mensaje.
   *
   * Sólo se le cree a FlotaBot y sólo a un marco de acá: el origen y la
   * ventana se miran los dos. En «Ver como» no se escribe nada: se está
   * mirando como otro, no trabajando. El mismo correo avisado dos veces a la
   * vez escribe una sola (y dos veces seguidas, la segunda ve que ya está).
   * El servicio se carga recién con el primer aviso: el armazón no paga por
   * Supabase y las planillas en cada pantalla que no es Enganches. */
  const verComo = useRef(verComoMail);
  verComo.current = verComoMail;
  useEffect(() => {
    const enCurso = new Set();
    const alMensaje = async (ev) => {
      if (ev.origin !== FLOTABOT) return;
      const d = ev.data;
      if (!d || d.fibot !== "enganche-correo" || !d.enganche) return;
      const marco = Object.values(marcos.current).find((el) => el?.contentWindow === ev.source);
      if (!marco || verComo.current) return;
      const clave = String(d.enganche.id ?? "");
      if (enCurso.has(clave)) return;
      enCurso.add(clave);
      let r;
      try {
        const { alGenerarCorreo } = await import("../../services/salidasSeparar");
        r = await alGenerarCorreo(d.enganche);
      } catch {
        r = {
          texto: String(d.enganche.sentido ?? "").trim().toLowerCase() === "sur"
            ? "No pude separar el bitren en SALIDAS BITREN. Volvé a generar el correo para probar de nuevo."
            : "No pude unificar el bitren en ARRIBOS BITREN. Volvé a generar el correo para probar de nuevo.",
        };
      } finally {
        enCurso.delete(clave);
      }
      if (!r?.texto) return;
      try {
        ev.source.postMessage(
          { fibot: "arribos-bitren", id: d.enganche.id, texto: r.texto, unificado: !!r.unificado },
          FLOTABOT,
        );
      } catch {
        // El marco ya se fue a otra página: la planilla quedó igual.
      }
    };
    window.addEventListener("message", alMensaje);
    return () => window.removeEventListener("message", alMensaje);
  }, []);

  /* ── EDITAR UN ARTÍCULO DESDE LA PÁGINA DE LA SEDE (Nico, 23/10) ─────────
   *
   * La solapa STOCK de una sede («Oficina de Nico · Madre», «BsAs») es de
   * FlotaBot: la tabla CÓDIGO | QUÉ ES | MARCA | SALDO con su «Quitar». Es
   * donde Nico mira sus artículos, y es ahí donde ve que uno escaneado se
   * llama «6935482103519 HPCF283A». Mandarlo al padrón de Stock a buscarlo de
   * nuevo es el mismo error que ya cometimos tres veces: el botón que no está
   * donde se hace el trabajo.
   *
   * NO SE ESCRIBE UN SEGUNDO EDITOR EN FLOTABOT. La página de la sede avisa
   * por mensaje qué artículo quiere editar (`{fibot: "editar-articulo", id}`)
   * y el hub abre ENCIMA el mismo `FormStock` de la ficha —descripción,
   * marca, unidad, tipo, detalle y códigos de barras—, con la sesión de quien
   * mira. Al guardar le contesta `{fibot: "articulo-editado", id, articulo}`
   * y la sede corrige el renglón sin recargar. Dos editores serían dos reglas
   * de qué es obligatorio y dos formas de leer un código de barras.
   *
   * La puerta es la misma que la del padrón de Stock: `permisoEditar` del
   * menú (`cargar.panol`), que es también lo que la página de la sede pide
   * para CARGAR y QUITAR y lo que la RLS de `stock` pide para escribir. Sin
   * permisos leídos todavía (`null`) no se abre: mejor un «probá en un
   * segundo» que un formulario que rebota al guardar. En «Ver como» tampoco:
   * se está mirando como otro, no trabajando. Igual que el enganche, sólo se
   * le cree a FlotaBot y a un marco de acá. */
  const [editandoArticulo, setEditandoArticulo] = useState(null);
  const puedeStock = useRef(false);
  puedeStock.current =
    Array.isArray(permisos) &&
    puedeEditar(
      SECCIONES.flatMap((g) => g.items).find((i) => i.id === "stock"),
      permisos,
    );
  useEffect(() => {
    const contestar = (fuente, msj) => {
      try {
        fuente.postMessage({ fibot: "articulo-editado", ...msj }, FLOTABOT);
      } catch {
        // La sede ya se fue a otra página: el artículo quedó guardado igual.
      }
    };
    const alMensaje = async (ev) => {
      if (ev.origin !== FLOTABOT) return;
      const d = ev.data;
      if (!d || d.fibot !== "editar-articulo" || !d.id) return;
      const marco = Object.values(marcos.current).find((el) => el?.contentWindow === ev.source);
      if (!marco) return;
      const id = String(d.id).trim().toUpperCase();
      if (verComo.current) {
        contestar(ev.source, { id, error: "En «Ver como» no se edita: se está mirando como otra persona." });
        return;
      }
      if (!puedeStock.current) {
        contestar(ev.source, { id, error: "Editar artículos pide el permiso de cargar en el pañol." });
        return;
      }
      let articulo = null;
      try {
        // Recién acá: el armazón no carga Supabase en cada pantalla por un
        // aviso que llega desde dos páginas de todo el panel.
        const { getRow } = await import("../../services/supabaseRealtime");
        articulo = await getRow("stock", id);
      } catch {
        articulo = null;
      }
      if (!articulo) {
        contestar(ev.source, { id, error: `No encontré el artículo ${id} en Stock.` });
        return;
      }
      setEditandoArticulo({ articulo, fuente: ev.source });
    };
    window.addEventListener("message", alMensaje);
    return () => window.removeEventListener("message", alMensaje);
  }, []);

  // El canje se dispara la primera vez que se abre una sección, no al arrancar
  // la app: quien nunca entra al panel no tiene por qué pagar ese viaje.
  useEffect(() => {
    if (!seccion || sesionLista) return;
    let vigente = true;
    abrirSesionEnFlotaBot().finally(() => {
      // Se sigue adelante aunque falle: en el peor caso se ve el login de
      // FlotaBot, que es exactamente como estaba antes. Bloquear el panel por
      // no poder ahorrar un login sería peor que el problema.
      if (vigente) setSesionLista(true);
    });
    return () => {
      vigente = false;
    };
  }, [seccion, sesionLista]);

  // Se anota la visita y se descartan las más viejas si se pasó del tope.
  if (item && !visitadas.current.includes(seccion)) {
    visitadas.current = [...visitadas.current, seccion].slice(-TOPE_VIVAS);
  } else if (item) {
    visitadas.current = [
      ...visitadas.current.filter((s) => s !== seccion),
      seccion,
    ];
  }

  // Sin sección activa no se muestra nada, pero los iframes ya cargados siguen
  // montados y escondidos: eso es lo que hace que volver sea instantáneo.
  const vivas = visitadas.current;
  if (!seccion && vivas.length === 0) return null;

  if (seccion && !item) {
    return (
      <div className="panel-aviso">
        <h3>Esa sección no existe</h3>
        <p>Elegí una del menú de la izquierda.</p>
      </div>
    );
  }

  /* Al `body` y no adentro del marco: `.panel-marco` recorta lo que se le
     sale (`overflow: hidden`) y el formulario tiene que tapar la pantalla
     entera, no quedar metido en el rectángulo del panel. */
  const editorDeArticulo = editandoArticulo && createPortal(
    <Suspense fallback={null}>
      <FormStock
        elemento={editandoArticulo.articulo}
        onGuardar={(guardado) => {
          const { articulo, fuente } = editandoArticulo;
          try {
            fuente.postMessage(
              {
                fibot: "articulo-editado",
                id: articulo.id,
                articulo: paraLaSede({ ...articulo, ...(guardado || {}) }),
              },
              FLOTABOT,
            );
          } catch {
            // La sede ya se fue a otra página: el artículo quedó guardado igual.
          }
        }}
        onClose={() => setEditandoArticulo(null)}
      />
    </Suspense>,
    document.body,
  );

  return (
    <div className={"panel-marco" + (seccion ? "" : " guardado")}>
      {editorDeArticulo}
      {seccion && !cargadas[seccion] && (
        <div className="panel-cargando">Abriendo {item.label}…</div>
      )}

      {sesionLista &&
        vivas.map((id) => {
          const s = SECCIONES.flatMap((g) => g.items).find((i) => i.id === id);
          if (!s) return null;
          const visible = id === seccion;
          const pedidoDelMarco = pedidos[id];
          return (
            <iframe
              key={pedidoDelMarco ? `${id}:${pedidoDelMarco.vez}` : id}
              src={
                FLOTABOT +
                s.ruta +
                (s.ruta.includes("?") ? "&" : "?") +
                "tema=" +
                temaInicial.current +
                (estiloInicial.current
                  ? "&estilo=" + encodeURIComponent(estiloInicial.current)
                  : "") +
                (verComoMail
                  ? "&ver_como=" + encodeURIComponent(verComoMail)
                  : "") +
                (pedidoDelMarco
                  ? "&ver=" + encodeURIComponent(pedidoDelMarco.ver)
                  : "")
              }
              title={s.label}
              className="panel-iframe"
              // Se esconde en vez de desmontarse. `display:none` haría que
              // algunos navegadores descarten el contexto y el mapa tendría que
              // rearmarse; con visibilidad y tamaño se mantiene vivo.
              style={
                visible
                  ? undefined
                  : { visibility: "hidden", position: "absolute", inset: 0 }
              }
              aria-hidden={!visible}
              ref={(el) => {
                if (el) marcos.current[id] = el;
                else delete marcos.current[id];
              }}
              onLoad={() => {
                setCargadas((c) => ({ ...c, [id]: true }));
                // Un marco recién cargado puede haber nacido con un tema que ya
                // cambió mientras cargaba. Se le repite el actual.
                avisarTema(temaActual());
              }}
              // Se le permite lo que necesita para funcionar (sus propios
              // scripts, formularios y sesión) y nada más.
              // `allow-top-navigation-to-custom-protocols` deja abrir un link
              // `mailto:` (el «Abrir en mi correo» de los enganches de bitren,
              // Lautaro 15/10): sin él Chrome lo bloquea adentro del iframe y
              // el botón no hace nada. Sólo protocolos que no son web
              // (mailto:, tel:): no cambia la página del hub.
              // `allow-popups` es para los links que el panel abre en otra
              // pestaña a propósito (`target="_blank"`): el «📲 WhatsApp» del
              // chofer en Avisos de ruta (Nico, 21/10, video: el click no
              // hacía nada y «abrir en otra pestaña» sí andaba), «ver en el
              // mapa»… Sin él el navegador los descarta sin avisar.
              // `allow-popups-to-escape-sandbox`: la pestaña nueva no hereda
              // este sandbox (WhatsApp Web no anda con él). La sección sigue
              // adentro del hub: esto no la saca a otra pestaña, y el panel
              // sigue sin poder cambiarle la página al hub.
              sandbox="allow-same-origin allow-scripts allow-forms allow-downloads allow-top-navigation-to-custom-protocols allow-popups allow-popups-to-escape-sandbox"
              // El mapa pide ubicación en algunas vistas. Y el portapapeles,
              // sólo para ESCRIBIR: «Copiar imagen» de los enganches de bitren
              // (la salvedad y el lastre son una imagen, y una imagen no tiene
              // otra forma de copiarse desde un botón). Leerlo, no.
              allow="geolocation; clipboard-write"
            />
          );
        })}
    </div>
  );
};

export default PanelesFlotaBot;
